/**
 * The zones a facility may be set to: the United States only, since that is
 * where every client's facilities are, and a list of every IANA zone was too
 * long to pick from. Ordered east to west, the zone most of a region uses
 * first and the local exceptions (Indiana, Kentucky, the Dakotas, Alaska's
 * panhandle) after it. It includes every zone the ZIP code suggestion
 * (`GET /timezones/suggestion`) can return, so a suggestion is always listed.
 */
export const US_TIME_ZONES: readonly string[] = [
  // Eastern
  'America/New_York',
  'America/Detroit',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/Indiana/Indianapolis',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Vevay',
  // Central
  'America/Chicago',
  'America/Indiana/Tell_City',
  'America/Indiana/Knox',
  'America/Menominee',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/North_Dakota/Beulah',
  // Mountain
  'America/Denver',
  'America/Boise',
  'America/Phoenix',
  // Pacific
  'America/Los_Angeles',
  // Alaska
  'America/Anchorage',
  'America/Juneau',
  'America/Sitka',
  'America/Metlakatla',
  'America/Yakutat',
  'America/Nome',
  'America/Adak',
  // Hawaii
  'Pacific/Honolulu',
  // Territories
  'America/Puerto_Rico',
  'America/St_Thomas',
  'Pacific/Guam',
  'Pacific/Saipan',
  'Pacific/Pago_Pago',
]

export function timeZoneOptions(): readonly string[] {
  return US_TIME_ZONES
}

/** The zone this browser is in, as a starting point for a facility near the person setting it up. */
export function browserTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null
  } catch {
    return null
  }
}

interface TimeZoneSelectProps {
  id?: string
  label: string
  /** An IANA zone name, or '' for "not set", which the API reads as UTC. */
  value: string
  onChange: (value: string) => void
  className?: string
  disabled?: boolean
}

/**
 * A plain select over the US zones, because a facility's zone is set once
 * and rarely changed. A stored zone outside the list (an old alias such as
 * America/Shiprock, UTC, or a zone set before the list was narrowed) stays
 * selectable so saving an unrelated field never silently changes it.
 */
export function TimeZoneSelect({ id, label, value, onChange, className, disabled }: TimeZoneSelectProps) {
  const options = timeZoneOptions()
  const zones = value !== '' && !options.includes(value) ? [value, ...options] : options
  return (
    <select id={id} aria-label={label} className={className} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
      <option value="">Not set (reads as UTC)</option>
      {zones.map((zone) => (
        <option key={zone} value={zone}>
          {zone.replaceAll('_', ' ')}
        </option>
      ))}
    </select>
  )
}
